import { createContext, useContext, useState, ReactNode, useMemo } from 'react';

export interface NotificationItem {
  notificationId: string;
  label: string;
  value?: Record<string, any>;
  status?: string;
  readStatus?: boolean;
  date?: string;
  [key: string]: any;
}

interface NotificationContextProps {
  notifications: NotificationItem[];
  processedNotificationId: string | null;
  processedNotification: NotificationItem | null;
  isNotificationPanelOpen: boolean;
  notificationFetchingStarted: boolean;
  notificationCount: number;
  totalNotificationCount: number;
  externalNotificationCount: number;
  setNotificationPanelOpen: (open: boolean) => void;
  setNotificationFetchingStarted: (started: boolean) => void;
  setExternalNotificationCount: (count: number) => void;
  addNotifications: (newNotifications: NotificationItem[]) => void;
  updateNotification: (notificationId: string, updatedFields: Partial<NotificationItem>) => void;
  updateNotificationFields: (
    notificationId: string | number,
    fields: {
      readStatus?: boolean;
      is_active?: boolean;
      value?: Partial<NotificationItem['value']>;
    },
  ) => void;
  removeNotification: (id: string | number) => void;
  setProcessedNotificationId: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const NotificationContextProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [processedNotificationId, setProcessedNotificationId] = useState<string | null>(null);
  const [isNotificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [notificationFetchingStarted, setNotificationFetchingStarted] = useState(false);
  const [externalNotificationCount, setExternalNotificationCount] = useState(0);

  const notificationCount = useMemo(() => {
    return notifications.filter((n) => n.readStatus === false).length;
  }, [notifications]);

  const totalNotificationCount = useMemo(
    () => notificationCount + externalNotificationCount,
    [externalNotificationCount, notificationCount],
  );

  const createNotificationMap = (notifications: NotificationItem[]) => {
    return new Map(notifications.map((item) => [item.notificationId, item]));
  };

  const replaceExistingNotifications = (
    prev: NotificationItem[],
    newMap: Map<string, NotificationItem>,
  ): NotificationItem[] => {
    return prev.reduce<NotificationItem[]>((acc, item) => {
      const updated = newMap.get(item.notificationId);

      if (updated) {
        acc.push(updated);
      }

      return acc;
    }, []);
  };

  const getNewNotificationsOnly = (
    newNotifications: NotificationItem[],
    prev: NotificationItem[],
  ) => {
    return newNotifications.filter((n) => !prev.some((p) => p.notificationId === n.notificationId));
  };

  const addNotifications = (newNotifications: NotificationItem[]) => {
    setNotifications((prev) => {
      const newMap = createNotificationMap(newNotifications);

      const existingUpdated = replaceExistingNotifications(prev, newMap);

      const newOnly = getNewNotificationsOnly(newNotifications, prev);

      const updatedList = existingUpdated.concat(newOnly);

      return updatedList.sort(
        (a, b) => new Date(b.date ?? '').getTime() - new Date(a.date ?? '').getTime(),
      );
    });
  };

  const updateNotification = (notificationId: string, updatedFields: Partial<NotificationItem>) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.notificationId === notificationId
          ? { ...n, ...updatedFields, value: { ...n.value, ...updatedFields.value } }
          : n,
      ),
    );
  };

  const updateNotificationFields = (
    notificationId: string | number,
    {
      readStatus,
      is_active,
      value,
    }: {
      readStatus?: boolean;
      is_active?: boolean;
      value?: Partial<NotificationItem['value']>;
    },
  ) => {
    const idStr = String(notificationId);

    setNotifications((prev) =>
      prev.map((n) =>
        n.notificationId === idStr
          ? {
              ...n,
              readStatus: readStatus ?? n.readStatus,
              value: {
                ...n.value,
                ...value,
                read: readStatus ?? n.value?.read,
                view_manager: {
                  ...n.value?.view_manager,
                  is_active: is_active ?? n.value?.view_manager?.is_active,
                },
              },
            }
          : n,
      ),
    );
  };

  const removeNotification = (notificationId: string | number) => {
    const idStr = String(notificationId);
    setNotifications((prev) => prev.filter((n) => n.notificationId !== idStr));
    if (processedNotificationId === idStr) {
      setProcessedNotificationId(null);
    }
  };

  const processedNotification = useMemo(() => {
    return notifications.find((n) => n.notificationId === processedNotificationId) || null;
  }, [notifications, processedNotificationId]);

  const clearAllNotifications = () => {
    setNotifications([]);
    setProcessedNotificationId(null);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        processedNotificationId,
        processedNotification,
        isNotificationPanelOpen,
        notificationFetchingStarted,
        notificationCount,
        totalNotificationCount,
        externalNotificationCount,
        setNotificationPanelOpen,
        setNotificationFetchingStarted,
        setExternalNotificationCount,
        addNotifications,
        updateNotification,
        updateNotificationFields,
        removeNotification,
        setProcessedNotificationId,
        clearAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextProps => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotificationContext must be used within a NotificationContextProvider');
  }
  return ctx;
};
